import { DollarSign, Calendar, ShieldCheck, Award, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const features = [
  { icon: <DollarSign className="h-6 w-6 md:h-8 md:w-8 text-primary" />, title: "Precios Transparentes", description: "Conoce tu precio exacto antes de reservar. Sin sorpresas.", delay: "delay-100" },
  { icon: <Calendar className="h-6 w-6 md:h-8 md:w-8 text-primary" />, title: "Reserva Fácil en Línea", description: "Agenda en menos de 60 segundos desde tu teléfono o computadora.", delay: "delay-200" },
  { icon: <ShieldCheck className="h-6 w-6 md:h-8 md:w-8 text-primary" />, title: "Limpiadores de Confianza", description: "Profesionales verificados, asegurados y comprometidos con la calidad.", delay: "delay-300" },
  { icon: <Award className="h-6 w-6 md:h-8 md:w-8 text-primary" />, title: "Garantía del 200%", description: "Si algo no está limpio, lo arreglamos gratis. Sin preguntas.", delay: "delay-400" }
];

export const SpanishWhyUs = ({ cityName }: { cityName: string }) => {
  const { ref: headingRef, isVisible: headingVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <section className="py-24 bg-muted/20">
      <div className="container">
        <div ref={headingRef} className={cn("text-center mb-16 anim-fade-up", headingVisible && "is-visible")}>
          <h2 className="text-3xl md:text-5xl font-bold mb-4">¿Por Qué Elegirnos?</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Servicio diseñado para las familias de {cityName}. Transparencia, comodidad y calidad en cada visita.
          </p>
        </div>

        <div className="relative">
          {/* Swipe hint for mobile */}
          <div className="md:hidden absolute -top-10 right-0 text-xs font-medium text-muted-foreground flex items-center gap-1.5 opacity-70 animate-pulse">
            <span className="tracking-wide uppercase">Deslizar</span>
            <ArrowRight className="h-3.5 w-3.5" />
          </div>
          
          <div ref={cardsRef} className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 pb-4 -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible md:pb-0">
            {features.map((f, i) => (
              <div key={i} className={cn("w-[70vw] sm:w-[45vw] shrink-0 snap-center md:w-auto bg-background p-6 md:p-8 rounded-2xl border shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 anim-fade-up flex flex-col items-center text-center md:items-start md:text-left", f.delay, cardsVisible && "is-visible")}>
                <div className="mb-4 md:mb-6 inline-flex p-3 rounded-xl bg-primary/5 text-primary">{f.icon}</div>
                <h3 className="text-base md:text-xl font-bold mb-2 md:mb-3">{f.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div ref={ctaRef} className={cn("mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 anim-fade-up", ctaVisible && "is-visible")}>
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold text-lg px-12 py-8 h-auto rounded-2xl shadow-xl group w-full sm:w-auto">
            <a href={BOOKING_URL}>Reservar Mi Limpieza <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" /></a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 font-bold text-lg px-12 py-8 h-auto rounded-2xl w-full sm:w-auto">
            <a href={BOOKING_URL}>Ver Precio Ahora</a>
          </Button>
        </div>
      </div>
    </section>
  );
};
